import React, { useRef, useState } from 'react';
import { Paperclip, Send, Mic, Square, Loader2 } from 'lucide-react';
import { commsApi } from '@/utils/commsApi';
import { useVoiceRecorder, extForMime } from '@/hooks/useVoiceRecorder';

const ACCEPT = 'image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.pptx,.mp4,.mov,.mp3,.wav,.m4a,.ogg,.webm';
const DICTATION_MAX_SECONDS = 120;

export const Composer = ({ disabled, upload, onSend, onFile, onDictationError }) => {
  const [text, setText] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const fileRef = useRef(null);
  const areaRef = useRef(null);

  const resize = () => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  // Dictation: tap to record, tap again to stop; speech becomes text in the box (whisper-1 via Emergent key).
  const dictation = useVoiceRecorder({
    maxSeconds: DICTATION_MAX_SECONDS,
    onClip: async ({ blob, mimeType }) => {
      const file = new File([blob], `dictation.${extForMime(mimeType)}`, { type: mimeType.split(';')[0] });
      setTranscribing(true);
      try {
        const { text: spoken } = await commsApi.transcribe(file, navigator.language?.slice(0, 2));
        if (!spoken) { onDictationError?.('No speech detected'); return; }
        setText((prev) => (prev ? `${prev.replace(/\s+$/, '')} ${spoken}` : spoken));
        requestAnimationFrame(() => { resize(); areaRef.current?.focus(); });
      } catch (err) {
        onDictationError?.(err?.response?.data?.detail || 'Transcription failed');
      } finally {
        setTranscribing(false);
      }
    },
    onError: (err) => {
      if (err?.message === 'too_short') onDictationError?.('Keep recording a little longer');
      else if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') onDictationError?.('Microphone access was denied');
      else if (err?.message === 'unsupported') onDictationError?.('Voice recording is not supported in this browser');
      else onDictationError?.('Could not start recording');
    },
  });

  const toggleDictation = () => (dictation.isRecording ? dictation.stop() : dictation.start());

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText('');
    requestAnimationFrame(resize);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onPaste = (e) => {
    const file = e.clipboardData?.files?.[0];
    if (file) {
      e.preventDefault();
      onFile(file);
    }
  };

  const dictationLabel = dictation.isRecording
    ? `Listening… ${Math.round(dictation.elapsed)}s — tap to finish`
    : transcribing ? 'Turning speech into text…' : null;

  return (
    <div className="composer" data-testid="composer">
      {upload && (
        <div className="upload-row" data-testid="upload-progress">
          <span className="upload-name">Sending {upload.name}… {upload.progress}%</span>
          <div className="upload-progress"><span style={{ width: `${upload.progress}%` }} /></div>
        </div>
      )}
      {dictationLabel && (
        <div className={`dictation-status ${dictation.isRecording ? 'recording' : ''}`} data-testid="dictation-status">
          {dictation.isRecording ? <span className="rec-dot" /> : <Loader2 size={12} className="spin" />} {dictationLabel}
        </div>
      )}
      <div className="composer-row">
        <input
          ref={fileRef}
          type="file"
          hidden
          accept={ACCEPT}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = '';
          }}
          data-testid="file-input"
        />
        <button
          type="button"
          className="icon-btn"
          onClick={() => fileRef.current?.click()}
          disabled={disabled || Boolean(upload)}
          aria-label="Attach file"
          data-testid="attach-button"
        >
          <Paperclip size={18} />
        </button>
        <button
          type="button"
          className={`icon-btn dictate-btn ${dictation.isRecording ? 'recording' : ''}`}
          onClick={toggleDictation}
          disabled={disabled || transcribing}
          aria-pressed={dictation.isRecording}
          aria-label={dictation.isRecording ? 'Stop dictation' : 'Dictate a message'}
          title="Dictate: speak and your words appear here"
          data-testid="dictate-button"
        >
          {transcribing ? <Loader2 size={18} className="spin" /> : dictation.isRecording ? <Square size={16} /> : <Mic size={18} />}
        </button>
        <textarea
          ref={areaRef}
          rows={1}
          value={text}
          onChange={(e) => { setText(e.target.value); resize(); }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={dictation.isRecording ? 'Listening…' : 'Type or dictate a message'}
          disabled={disabled}
          data-testid="message-input"
        />
        <button
          type="button"
          className="icon-btn send-btn"
          onClick={submit}
          disabled={disabled || !text.trim()}
          aria-label="Send message"
          data-testid="send-button"
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};
